/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import {
  collection, query, where, getDocs,
  addDoc, deleteDoc, doc, serverTimestamp, limit
} from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// CitasLibro — guardar frases subrayadas de un libro y compartirlas
//
// Props:
//   userId    — UID del usuario
//   libroId   — ID del libro
//   libro     — { titulo, autor, portada }
//   esPremium — boolean
//   onUpgrade — función para abrir modal premium
//
// Colección Firestore (nueva, no toca nada existente):
//   citas/{citaId}
//     userId, libroId, titulo, autor, portada, texto, fecha
//
// Límite gratis: 10 citas en total (entre todos los libros)
// Premium: ilimitadas
// Tarjeta compartible: gratis con "Leemos" como watermark, premium sin él
// ─────────────────────────────────────────────────────────────────────────────

const MAX_CITAS_GRATIS = 10;
const MAX_TEXTO = 280;

// ── Paletas para la tarjeta ───────────────────────────────────────────────────
const PALETAS = [
  { id: 'oscuro',    bg: '#0a0a0a', texto: '#ffffff', acento: '#1DB954', nombre: 'Oscuro' },
  { id: 'noche',    bg: '#0d1117', texto: '#e6edf3', acento: '#58a6ff', nombre: 'Noche' },
  { id: 'sepia',    bg: '#1a1410', texto: '#d4c5a9', acento: '#c9a96e', nombre: 'Sepia' },
  { id: 'bosque',   bg: '#0d1f0d', texto: '#d4edda', acento: '#4caf50', nombre: 'Bosque' },
  { id: 'aurora',   bg: '#12001e', texto: '#f0d6ff', acento: '#a78bfa', nombre: 'Aurora' },
];

// ── Genera SVG de la tarjeta compartible ─────────────────────────────────────
function generarSVGTarjeta({ texto, titulo, autor, portada, paleta, esPremium }) {
  const p = PALETAS.find(x => x.id === paleta) || PALETAS[0];
  const W = 800, H = 440;

  // Partir texto en líneas de ~48 chars
  const palabras = texto.split(' ');
  const lineas = [];
  let lineaActual = '';
  for (const palabra of palabras) {
    if ((lineaActual + ' ' + palabra).trim().length > 48) {
      if (lineaActual) lineas.push(lineaActual.trim());
      lineaActual = palabra;
    } else {
      lineaActual = (lineaActual + ' ' + palabra).trim();
    }
  }
  if (lineaActual) lineas.push(lineaActual.trim());
  const lineasMostrar = lineas.slice(0, 6);
  const textoSVG = lineasMostrar
    .map((l, i) => `<tspan x="80" dy="${i === 0 ? 0 : 36}">${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</tspan>`)
    .join('');

  const textoY = Math.max(140, 200 - lineasMostrar.length * 18);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${p.bg}"/>
      <stop offset="100%" stop-color="${p.bg}ee"/>
    </linearGradient>
  </defs>

  <!-- Fondo -->
  <rect width="${W}" height="${H}" fill="url(#bg)" rx="0"/>

  <!-- Borde sutil -->
  <rect width="${W}" height="${H}" fill="none" stroke="${p.acento}" stroke-width="2" rx="0" opacity="0.2"/>

  <!-- Comilla apertura -->
  <text x="52" y="100" font-size="120" fill="${p.acento}" opacity="0.25" font-family="Georgia, serif">"</text>

  <!-- Texto de la cita -->
  <text
    font-family="Georgia, 'Times New Roman', serif"
    font-size="22"
    fill="${p.texto}"
    opacity="0.95"
    font-style="italic"
    letter-spacing="0.3"
  >
    <tspan x="80" y="${textoY}">${textoSVG.includes('<tspan') ? '' : texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</tspan>
    ${textoSVG.includes('<tspan') ? textoSVG.replace('<tspan x="80" dy="0">', `<tspan x="80" y="${textoY}">`) : ''}
  </text>

  <!-- Línea separadora -->
  <line x1="80" y1="${textoY + lineasMostrar.length * 36 + 28}" x2="180" y2="${textoY + lineasMostrar.length * 36 + 28}" stroke="${p.acento}" stroke-width="2" opacity="0.6"/>

  <!-- Título y autor del libro -->
  <text x="80" y="${textoY + lineasMostrar.length * 36 + 58}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="15" fill="${p.acento}" font-weight="bold" opacity="0.9">
    ${titulo.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').slice(0, 50)}
  </text>
  <text x="80" y="${textoY + lineasMostrar.length * 36 + 80}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="13" fill="${p.texto}" opacity="0.5">
    ${autor.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').slice(0, 40)}
  </text>

  <!-- Logo Leemos (siempre visible, más pequeño en premium) -->
  ${esPremium
    ? `<text x="${W - 20}" y="${H - 16}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="11" fill="${p.texto}" opacity="0.2" text-anchor="end">Leemos</text>`
    : `<rect x="${W - 110}" y="${H - 38}" width="90" height="26" rx="13" fill="${p.acento}" opacity="0.15"/>
       <text x="${W - 65}" y="${H - 21}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="13" fill="${p.acento}" font-weight="bold" text-anchor="middle" opacity="0.9">📚 Leemos</text>`
  }
</svg>`;
}

// ── Modal tarjeta compartible ─────────────────────────────────────────────────
function ModalTarjeta({ cita, libro, esPremium, onCerrar }) {
  const [paleta, setPaleta] = useState('oscuro');
  const [copiado, setCopiado] = useState(false);
  const svgRef = useRef(null);

  const svg = generarSVGTarjeta({
    texto: cita.texto,
    titulo: libro?.titulo || cita.titulo || '',
    autor: libro?.autor || cita.autor || '',
    portada: libro?.portada || '',
    paleta,
    esPremium,
  });

  const descargar = () => {
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leemos-cita-${Date.now()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copiarSVG = async () => {
    try {
      await navigator.clipboard.writeText(svg);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { }
  };

  // Intentar copiar como PNG usando Canvas
  const compartirImagen = async () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 440;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      img.onload = async () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob(async (pngBlob) => {
          try {
            if (navigator.share && navigator.canShare({ files: [new File([pngBlob], 'leemos.png', { type: 'image/png' })] })) {
              await navigator.share({
                files: [new File([pngBlob], 'leemos.png', { type: 'image/png' })],
                title: `"${cita.texto.slice(0, 60)}..."`,
                text: `— ${libro?.titulo || cita.titulo}, ${libro?.autor || cita.autor} | Leemos`,
              });
            } else {
              // Fallback: descargar PNG
              const pngUrl = URL.createObjectURL(pngBlob);
              const a = document.createElement('a');
              a.href = pngUrl;
              a.download = `leemos-cita-${Date.now()}.png`;
              a.click();
              URL.revokeObjectURL(pngUrl);
            }
          } catch { descargar(); }
        }, 'image/png');
      };
      img.onerror = descargar;
      img.src = url;
    } catch { descargar(); }
  };

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal-content" style={{ maxWidth: '520px', padding: '24px' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900 }}>Tarjeta de cita</h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>✕</button>
        </div>

        {/* Preview SVG */}
        <div style={{ borderRadius: '10px', overflow: 'hidden', marginBottom: '16px', border: '1px solid #1e1e1e' }}
          ref={svgRef}
          dangerouslySetInnerHTML={{ __html: svg }}
        />

        {/* Selector de paleta */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 10px', fontWeight: 'bold' }}>Color</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            {PALETAS.map(p => (
              <button key={p.id} onClick={() => setPaleta(p.id)}
                style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: p.bg,
                  border: paleta === p.id ? `3px solid ${p.acento}` : '2px solid #2a2a2a',
                  cursor: 'pointer', transition: '0.15s',
                  boxShadow: paleta === p.id ? `0 0 8px ${p.acento}66` : 'none',
                }}
                title={p.nombre}
              />
            ))}
          </div>
        </div>

        {/* Nota premium si no lo es */}
        {!esPremium && (
          <div style={{ background: '#12001e', border: '1px solid #a78bfa33', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', color: '#7c5fad' }}>La tarjeta incluye el logo de Leemos. Premium lo elimina.</span>
          </div>
        )}

        {/* Botones */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={compartirImagen}
            style={{ flex: 1, background: '#1DB954', color: 'black', border: 'none', borderRadius: '25px', padding: '12px', fontWeight: 900, cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z"/></svg>
            Compartir
          </button>
          <button onClick={descargar}
            style={{ background: 'transparent', color: '#aaa', border: '1px solid #2a2a2a', borderRadius: '25px', padding: '12px 16px', cursor: 'pointer', fontSize: '13px', transition: '0.2s' }}
            title="Descargar SVG">
            ↓ SVG
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export function CitasLibro({ userId, libroId, libro, esPremium, onUpgrade }) {
  const [citas, setCitas] = useState([]);
  const [totalCitasUsuario, setTotalCitasUsuario] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [textoCita, setTextoCita] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [citaEnTarjeta, setCitaEnTarjeta] = useState(null);
  const [recargar, setRecargar] = useState(0);

  // Cargar citas de este libro
  useEffect(() => {
    if (!userId || !libroId) { setCargando(false); return; }
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, 'citas'),
            where('userId', '==', userId),
            where('libroId', '==', libroId),
            limit(50)
          )
        );
        if (activo) setCitas(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.fecha?.toMillis?.() || 0) - (a.fecha?.toMillis?.() || 0))
        );
      } catch { }
      if (activo) setCargando(false);
    };
    cargar();
    return () => { activo = false; };
  }, [userId, libroId, recargar]);

  // Contar total de citas del usuario (para límite gratis)
  useEffect(() => {
    if (!userId || esPremium) return;
    let activo = true;
    const contar = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'citas'), where('userId', '==', userId), limit(MAX_CITAS_GRATIS + 1))
        );
        if (activo) setTotalCitasUsuario(snap.size);
      } catch { }
    };
    contar();
    return () => { activo = false; };
  }, [userId, esPremium, recargar]);

  const puedeGuardar = esPremium || totalCitasUsuario < MAX_CITAS_GRATIS;

  const guardarCita = async () => {
    const texto = textoCita.trim();
    if (!texto || guardando) return;
    if (!puedeGuardar) { onUpgrade(); return; }
    setGuardando(true);
    try {
      await addDoc(collection(db, 'citas'), {
        userId,
        libroId,
        titulo: libro?.titulo || '',
        autor: libro?.autor || '',
        portada: libro?.portada || '',
        texto,
        fecha: serverTimestamp(),
      });
      setTextoCita('');
      setAbierto(false);
      setRecargar(r => r + 1);
    } catch (e) { console.error(e); }
    setGuardando(false);
  };

  const borrarCita = async (citaId) => {
    try {
      await deleteDoc(doc(db, 'citas', citaId));
      setCitas(prev => prev.filter(c => c.id !== citaId));
      setTotalCitasUsuario(prev => Math.max(0, prev - 1));
    } catch { }
  };

  return (
    <>
      {/* Modal tarjeta */}
      {citaEnTarjeta && (
        <ModalTarjeta
          cita={citaEnTarjeta}
          libro={libro}
          esPremium={esPremium}
          onCerrar={() => setCitaEnTarjeta(null)}
        />
      )}

      <div style={{
        background: '#111',
        border: '1px solid #1e1e1e',
        borderRadius: '14px',
        padding: '16px',
        margin: '14px 0',
      }}>
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: abierto || citas.length > 0 ? '14px' : '0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#a78bfa">
              <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z"/>
            </svg>
            <span style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>
              Mis citas
            </span>
            {citas.length > 0 && (
              <span style={{ background: '#a78bfa22', color: '#a78bfa', fontSize: '10px', fontWeight: 'bold', padding: '1px 7px', borderRadius: '10px' }}>
                {citas.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setAbierto(v => !v)}
            style={{
              background: abierto ? 'transparent' : '#a78bfa22',
              color: '#a78bfa',
              border: abierto ? '1px solid #2a2a2a' : '1px solid #a78bfa44',
              borderRadius: '20px',
              padding: '5px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              transition: '0.15s',
            }}
          >
            {abierto ? 'Cancelar' : '+ Añadir cita'}
          </button>
        </div>

        {/* Formulario añadir cita */}
        {abierto && (
          <div style={{ marginBottom: '14px' }}>
            {/* Aviso límite gratis */}
            {!esPremium && (
              <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '11px', color: totalCitasUsuario >= MAX_CITAS_GRATIS ? '#f44336' : '#555' }}>
                  {totalCitasUsuario}/{MAX_CITAS_GRATIS} citas gratis
                </span>
                {totalCitasUsuario >= MAX_CITAS_GRATIS && (
                  <button onClick={onUpgrade}
                    style={{ background: 'linear-gradient(135deg, #6a3fa0, #a78bfa)', color: 'white', border: 'none', borderRadius: '12px', padding: '4px 10px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                    ✦ Premium — ilimitadas
                  </button>
                )}
              </div>
            )}
            <textarea
              autoFocus
              value={textoCita}
              onChange={e => setTextoCita(e.target.value.slice(0, MAX_TEXTO))}
              placeholder={puedeGuardar ? '"Escribe aquí la frase que te ha marcado..."' : 'Has alcanzado el límite de citas gratis.'}
              disabled={!puedeGuardar}
              style={{
                width: '100%',
                background: '#0a0a0a',
                border: '1px solid #2a2a2a',
                borderRadius: '10px',
                padding: '12px',
                color: 'white',
                fontSize: '14px',
                fontStyle: 'italic',
                resize: 'none',
                height: '90px',
                outline: 'none',
                boxSizing: 'border-box',
                lineHeight: '1.6',
                opacity: puedeGuardar ? 1 : 0.4,
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
              <span style={{ fontSize: '11px', color: textoCita.length > MAX_TEXTO * 0.85 ? '#f59e0b' : '#333' }}>
                {textoCita.length}/{MAX_TEXTO}
              </span>
              <button
                onClick={guardarCita}
                disabled={!textoCita.trim() || guardando || !puedeGuardar}
                style={{
                  background: textoCita.trim() && puedeGuardar ? '#a78bfa' : '#1a1a1a',
                  color: textoCita.trim() && puedeGuardar ? 'white' : '#333',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 20px',
                  fontWeight: 'bold',
                  cursor: textoCita.trim() && puedeGuardar ? 'pointer' : 'default',
                  fontSize: '13px',
                  transition: '0.2s',
                }}
              >
                {guardando ? 'Guardando...' : 'Guardar cita'}
              </button>
            </div>
          </div>
        )}

        {/* Lista de citas guardadas */}
        {cargando ? (
          <div style={{ height: '40px', background: '#1a1a1a', borderRadius: '8px', animation: 'pulse 1.5s infinite' }} />
        ) : citas.length === 0 && !abierto ? (
          <p style={{ fontSize: '12px', color: '#333', margin: 0, fontStyle: 'italic' }}>
            Guarda las frases que te marquen mientras lees.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {citas.map((cita) => (
              <div key={cita.id} style={{
                background: '#0a0a0a',
                border: '1px solid #1a1a1a',
                borderRadius: '10px',
                padding: '12px 14px',
                position: 'relative',
              }}>
                {/* Comilla decorativa */}
                <span style={{ position: 'absolute', top: '6px', left: '10px', fontSize: '28px', color: '#a78bfa', opacity: 0.2, lineHeight: 1, fontFamily: 'Georgia, serif' }}>"</span>

                <p style={{
                  margin: '0 0 8px',
                  fontSize: '13px',
                  color: '#ccc',
                  fontStyle: 'italic',
                  lineHeight: '1.6',
                  paddingLeft: '14px',
                }}>
                  {cita.texto}
                </p>

                {/* Acciones */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                  {/* Botón tarjeta */}
                  <button
                    onClick={() => setCitaEnTarjeta(cita)}
                    style={{
                      background: '#a78bfa22',
                      color: '#a78bfa',
                      border: '1px solid #a78bfa33',
                      borderRadius: '14px',
                      padding: '4px 10px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: '0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#a78bfa33'}
                    onMouseLeave={e => e.currentTarget.style.background = '#a78bfa22'}
                    title="Crear tarjeta compartible"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z"/></svg>
                    Tarjeta
                  </button>

                  {/* Botón borrar */}
                  <button
                    onClick={() => borrarCita(cita.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '2px 4px',
                      lineHeight: 1,
                      transition: '0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#f44336'}
                    onMouseLeave={e => e.currentTarget.style.color = '#333'}
                    title="Borrar cita"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
